import { handleNetworkResponseAndGetJsonAsync, Network } from './Network'
import { GlobalVars } from '../../global'
import { CreateProductRequest } from './collections/product/createProduct/createProduct_post'
import { CreateProductResponse } from './collections/product/createProduct/createProduct_200'
import { GetProductsResponse } from './collections/product/getListOfProducts/getListOfProducts_200'
import { GetProductEventsResponse } from './collections/product/getEventsOfProduct/getEventsOfProduct_200'
import { GetProductResponse } from './collections/product/getProductDetail/getProductDetail_200'
import { UpdateProductRequest } from './collections/product/updateProduct/updateProduct_post'
import { UpdateProductResponse } from './collections/product/updateProduct/updateProduct_200'
import { GetProductQuantitiesRequest } from './collections/product/getProductQuantities/getProductQuantities_200'
import { removeTimezone } from '../../utils/global-utils'
import { DatabaseLookupResponse } from './sharedTypes/Common'

export class ProductManager {
    static _instance: ProductManager | null = null

    static getManager(): ProductManager {
        if (this._instance === null) {
            this._instance = new ProductManager()
        }
        return this._instance
    }

    async createProductAsync(product: CreateProductRequest): Promise<CreateProductResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/product/create`
        const body: CreateProductRequest = product
        try {
            response = await Network.fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: CreateProductResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getProductListAsync(): Promise<GetProductsResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/product`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: GetProductsResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getAllFilteredProductsAsync(dateFrom: string, dateTo: string): Promise<GetProductsResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/product?EventsFrom=${dateFrom}&EventsTo=${dateTo}`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: GetProductsResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getEventsOfProductAsync(id: string, EventsFrom?: Date, EventsTo?: Date): Promise<GetProductEventsResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/product/${id}/event${
            (EventsFrom || EventsTo ? '?' : '') +
            (EventsFrom ? `EventsFrom=${removeTimezone(EventsFrom)}` : '') +
            (EventsFrom && EventsTo ? '&' : '') +
            (EventsTo ? `EventsTo=${removeTimezone(EventsTo)}` : '')
        }`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: GetProductEventsResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getProductAsync(id: string): Promise<GetProductResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/product/${id}`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: GetProductResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async deleteProductAsync(id: string): Promise<void> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/product/${id}`
        try {
            response = await Network.fetch(url, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: void = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async updateProductAsync(id: string, product: UpdateProductRequest): Promise<UpdateProductResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/product/update/${id}`
        const body: UpdateProductRequest = product
        try {
            response = await Network.fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: UpdateProductResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getProductQuantitiesAsync(id: string): Promise<GetProductQuantitiesRequest> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/product/${id}/count`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: GetProductQuantitiesRequest = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getProductsLookupAsync(): Promise<DatabaseLookupResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/product/lookup?canBeAssigned`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: DatabaseLookupResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }
}
